export type Identifier = string | number;

export type ApiRecord = Record<string, unknown>;

export interface Branch {
  id: Identifier;
  code: string;
  name: string;
  address: string;
  whatsappNumber: string;
  openingTime: string;
  closingTime: string;
  status: "active" | "inactive" | "temporarily_closed";
  description?: string;
  isDefault: boolean;
  shippingV2Enabled: boolean;
  shippingV2FreeRadiusEnabled: boolean;
  shippingV2FreeRadiusKm?: number;
  delivery: DeliverySettings;
}

export interface Category {
  id: Identifier;
  slug: string;
  name: string;
  description?: string;
  mediaId?: Identifier;
  navigationOrder?: number;
}

export interface Product {
  id: Identifier;
  slug: string;
  name: string;
  description?: string;
  brand?: string;
  unit: string;
  price: number;
  promoPrice?: number;
  stock: number;
  minimumStock?: number;
  isUnlimited: boolean;
  isAvailable: boolean;
  isFeatured: boolean;
  mediaId?: Identifier;
  categoryId?: Identifier;
  categoryIds?: Identifier[];
  categoryName?: string;
  categorySlug?: string;
  branchId?: Identifier;
}

export interface HomepageSection {
  id: Identifier;
  title: string;
  subtitle?: string;
  type: string;
  categoryId?: Identifier;
  mediaId?: Identifier;
  ctaText?: string;
  ctaUrl?: string;
  products?: Product[];
}

export interface PaymentMethod {
  id: Identifier;
  name: string;
  type: string;
  instructions?: string;
  accountName?: string;
  accountNumber?: string;
  mediaId?: Identifier;
}

export interface DeliverySettings {
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
  feeType: "FLAT_FEE" | "FREE" | "MINIMUM_ORDER_FREE_DELIVERY";
  flatFee: number;
  freeDeliveryMinimum?: number;
  acceptOrdersOutsideHours: boolean;
  timeZone: "Asia/Jakarta" | "Asia/Makassar" | "Asia/Jayapura";
  timeZoneLabel: string;
  deliveryArea?: string;
  deliveryWindows: DeliveryWindow[];
  deliveryNote?: string;
}

export interface DeliveryWindow {
  label: string;
  startTime: string;
  endTime: string;
  cutoffTime: string;
}

export interface StoreBootstrap {
  storeName: string;
  storeTagline: string;
  liveStream?: {
    enabled: true;
    platform: "YOUTUBE" | "TIKTOK";
    originalUrl: string;
    embedUrl: string;
    title: string;
  };
  branches: Branch[];
  activeBranch?: Branch;
  categories: Category[];
  navigationCategories: Category[];
  allCategories: Category[];
  sections: HomepageSection[];
  products: Product[];
  paymentMethods: PaymentMethod[];
  deliverySettings: DeliverySettings;
  orderLookupMode: "ORDER_AND_PHONE" | "ORDER_ONLY" | "PHONE_ONLY";
  degraded?: boolean;
  degradedReason?: string;
}

export interface CartItem {
  product: Product;
  quantity: number;
}

export interface CartState {
  branchId?: Identifier;
  items: CartItem[];
}

export interface SessionUser {
  id: Identifier;
  name: string;
  username: string;
  role: "OWNER" | "ADMIN";
  branchId?: Identifier;
  branchName?: string;
}

export interface OrderLine {
  id?: Identifier;
  productId?: Identifier;
  productName: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

export interface Order {
  id?: Identifier;
  orderNumber: string;
  branchId?: Identifier;
  branchName: string;
  branchWhatsapp?: string;
  customerName: string;
  customerWhatsapp?: string;
  customerWhatsappMasked?: string;
  deliveryType: string;
  deliveryAddress?: string;
  subtotal: number;
  deliveryFee: number;
  discount: number;
  pointsRedeemed: number;
  pointsEarned: number;
  total: number;
  orderStatus: string;
  paymentStatus: string;
  paymentMethodName: string;
  createdAt?: string;
  updatedAt?: string;
  confirmedAt?: string;
  serviceTimeZone?: string;
  statusEvents: Array<{
    status: string;
    createdAt: string;
  }>;
  liveToken?: string;
  liveExpiresAt?: string;
  items: OrderLine[];
  whatsappUrl?: string;
  whatsappNotifications: Array<{
    recipientType: "BRANCH" | "OWNER" | "CUSTOMER";
    status: "PENDING" | "SENT" | "FAILED" | "MANUAL";
  }>;
}

export interface CollectionResult<T> {
  items: T[];
  total: number;
}

const record = (value: unknown): ApiRecord =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as ApiRecord)
    : {};

const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const valueFor = (source: ApiRecord, ...keys: string[]): unknown => {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key];
  }
  return undefined;
};

const text = (value: unknown, fallback = ""): string =>
  typeof value === "string" || typeof value === "number"
    ? String(value)
    : fallback;

const numeric = (value: unknown, fallback = 0): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const boolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return fallback;
};

function normalizeDeliveryWindows(value: unknown): DeliveryWindow[] {
  let source: unknown = value;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source) as unknown;
    } catch {
      return [];
    }
  }
  return list(source)
    .map((item) => {
      const window = record(item);
      return {
        label: text(valueFor(window, "label"), "Pengiriman"),
        startTime: text(valueFor(window, "startTime", "start_time")),
        endTime: text(valueFor(window, "endTime", "end_time")),
        cutoffTime: text(valueFor(window, "cutoffTime", "cutoff_time")),
      };
    })
    .filter(
      (window) =>
        Boolean(window.startTime) &&
        Boolean(window.endTime) &&
        Boolean(window.cutoffTime),
    );
}

function normalizeDelivery(value: unknown): DeliverySettings {
  const source = record(value);
  return {
    deliveryEnabled: boolean(
      valueFor(source, "deliveryEnabled", "delivery_enabled"),
      true,
    ),
    pickupEnabled: boolean(
      valueFor(source, "pickupEnabled", "pickup_enabled"),
      true,
    ),
    feeType: text(
      valueFor(source, "feeType", "fee_type"),
      "FLAT_FEE",
    ) as DeliverySettings["feeType"],
    flatFee: numeric(valueFor(source, "flatFee", "flat_fee"), 0),
    freeDeliveryMinimum:
      numeric(
        valueFor(source, "freeDeliveryMinimum", "free_delivery_minimum"),
        0,
      ) || undefined,
    acceptOrdersOutsideHours: boolean(
      valueFor(
        source,
        "acceptOrdersOutsideHours",
        "accept_orders_outside_hours",
      ),
      true,
    ),
    timeZone: text(
      valueFor(source, "timeZone", "iana_timezone"),
      "Asia/Makassar",
    ) as DeliverySettings["timeZone"],
    timeZoneLabel: text(
      valueFor(source, "timeZoneLabel", "time_zone_label"),
      "WITA",
    ),
    deliveryArea:
      text(valueFor(source, "deliveryArea", "delivery_area")) || undefined,
    deliveryWindows: normalizeDeliveryWindows(
      valueFor(source, "deliveryWindows", "delivery_windows_json"),
    ),
    deliveryNote:
      text(valueFor(source, "deliveryNote", "delivery_note")) || undefined,
  };
}

export function normalizeBranch(value: unknown): Branch {
  const source = record(value);
  const statusValue = text(valueFor(source, "status"), "active");
  const status: Branch["status"] =
    statusValue === "inactive" || statusValue === "temporarily_closed"
      ? statusValue
      : "active";
  return {
    id: text(valueFor(source, "id"), ""),
    code: text(valueFor(source, "code"), "CAB"),
    name: text(valueFor(source, "name"), "Cabang Warung Sedap Sayur"),
    address: text(valueFor(source, "address"), "Alamat cabang belum tersedia"),
    whatsappNumber: text(valueFor(source, "whatsappNumber", "whatsapp_number")),
    openingTime: text(valueFor(source, "openingTime", "opening_time"), "07:00"),
    closingTime: text(valueFor(source, "closingTime", "closing_time"), "21:00"),
    status,
    description: text(valueFor(source, "description")) || undefined,
    isDefault: boolean(valueFor(source, "isDefault", "is_default")),
    shippingV2Enabled: boolean(
      valueFor(source, "shippingV2Enabled", "shipping_v2_enabled"),
    ),
    shippingV2FreeRadiusEnabled: boolean(
      valueFor(
        source,
        "shippingV2FreeRadiusEnabled",
        "shipping_v2_free_radius_enabled",
      ),
    ),
    shippingV2FreeRadiusKm:
      valueFor(
        source,
        "shippingV2FreeRadiusKm",
        "shipping_v2_free_radius_km",
      ) === null ||
      valueFor(
        source,
        "shippingV2FreeRadiusKm",
        "shipping_v2_free_radius_km",
      ) === undefined
        ? undefined
        : numeric(
            valueFor(
              source,
              "shippingV2FreeRadiusKm",
              "shipping_v2_free_radius_km",
            ),
          ),
    delivery: normalizeDelivery(valueFor(source, "delivery") ?? source),
  };
}

export function normalizeCategory(value: unknown): Category {
  const source = record(value);
  return {
    id: text(valueFor(source, "id"), ""),
    slug: text(valueFor(source, "slug"), "kategori"),
    name: text(valueFor(source, "name"), "Kategori"),
    description: text(valueFor(source, "description")) || undefined,
    mediaId:
      (valueFor(source, "mediaId", "media_id") as Identifier | undefined) ??
      undefined,
    navigationOrder:
      valueFor(source, "navigationOrder", "navigation_order") === undefined ||
      valueFor(source, "navigationOrder", "navigation_order") === null
        ? undefined
        : numeric(valueFor(source, "navigationOrder", "navigation_order")),
  };
}

export function normalizeProduct(value: unknown): Product {
  const source = record(value);
  const promo = numeric(valueFor(source, "promoPrice", "promo_price"), 0);
  const stock = numeric(valueFor(source, "stock", "quantity"), 0);
  const categoryValues = valueFor(source, "categoryIds", "category_ids");
  const categoryIds = Array.isArray(categoryValues)
    ? categoryValues.filter(
        (item): item is Identifier =>
          typeof item === "string" || typeof item === "number",
      )
    : typeof categoryValues === "string"
      ? categoryValues.split(",").filter(Boolean)
      : [];
  return {
    id: text(valueFor(source, "id", "productId", "product_id"), ""),
    slug: text(
      valueFor(source, "slug"),
      `produk-${text(valueFor(source, "id"), "")}`,
    ),
    name: text(
      valueFor(source, "name", "productName", "product_name"),
      "Produk",
    ),
    description: text(valueFor(source, "description")) || undefined,
    brand: text(valueFor(source, "brand")) || undefined,
    unit: text(valueFor(source, "unit", "weight"), "pcs"),
    price: numeric(valueFor(source, "price", "unitPrice", "unit_price")),
    promoPrice: promo > 0 ? promo : undefined,
    stock,
    minimumStock: numeric(valueFor(source, "minimumStock", "minimum_stock"), 0),
    isUnlimited: boolean(valueFor(source, "isUnlimited", "is_unlimited")),
    isAvailable: boolean(
      valueFor(source, "isAvailable", "is_available"),
      boolean(valueFor(source, "isUnlimited", "is_unlimited")) || stock > 0,
    ),
    isFeatured: boolean(valueFor(source, "isFeatured", "is_featured")),
    mediaId:
      (valueFor(
        source,
        "mediaId",
        "media_id",
        "primaryMediaId",
        "primary_media_id",
      ) as Identifier | undefined) ?? undefined,
    categoryId:
      (valueFor(source, "categoryId", "category_id") as
        Identifier | undefined) ?? undefined,
    categoryIds,
    categoryName:
      text(valueFor(source, "categoryName", "category_name")) || undefined,
    categorySlug:
      text(valueFor(source, "categorySlug", "category_slug")) || undefined,
    branchId:
      (valueFor(source, "branchId", "branch_id") as Identifier | undefined) ??
      undefined,
  };
}

export function normalizeProductDetail(value: unknown): Product {
  const source = record(value);
  return normalizeProduct(valueFor(source, "product") ?? value);
}

export function normalizeProductCollection(
  value: unknown,
): CollectionResult<Product> {
  const source = record(value);
  const items = Array.isArray(value)
    ? value
    : list(valueFor(source, "items", "products", "results", "rows", "data"));
  return {
    items: items.map(normalizeProduct),
    total: numeric(valueFor(source, "total", "count"), items.length),
  };
}

export function normalizeBootstrap(value: unknown): StoreBootstrap {
  const source = record(value);
  const liveStreamSource = record(
    valueFor(source, "liveStream", "live_stream") ??
      valueFor(record(valueFor(source, "store")), "liveStream", "live_stream"),
  );
  const liveStreamPlatform = text(
    valueFor(liveStreamSource, "platform"),
  ).toUpperCase();
  const liveStreamEmbedUrl = text(
    valueFor(liveStreamSource, "embedUrl", "embed_url"),
  );
  const liveStreamOriginalUrl = text(
    valueFor(liveStreamSource, "originalUrl", "original_url"),
  );
  const branches = list(valueFor(source, "branches")).map(normalizeBranch);
  const activeRaw = valueFor(source, "activeBranch", "active_branch", "branch");
  const activeBranch = activeRaw
    ? normalizeBranch(activeRaw)
    : (branches.find((branch) => branch.isDefault) ?? branches[0]);
  const activeSource = record(activeRaw);
  const categories = list(valueFor(source, "categories")).map(normalizeCategory);
  const navigationCategoriesSource = valueFor(
    source,
    "navigationCategories",
    "navigation_categories",
  );
  const navigationCategories =
    navigationCategoriesSource === undefined
      ? categories
      : list(navigationCategoriesSource).map(normalizeCategory);
  const allCategoriesSource = valueFor(
    source,
    "allCategories",
    "all_categories",
  );
  const allCategories =
    allCategoriesSource === undefined
      ? Array.from(
          new Map(
            [...navigationCategories, ...categories].map((category) => [
              String(category.id || category.slug),
              category,
            ]),
          ).values(),
        )
      : list(allCategoriesSource).map(normalizeCategory);
  const delivery = record(
    valueFor(source, "deliverySettings", "delivery_settings") ??
      valueFor(activeSource, "delivery"),
  );
  return {
    storeName: text(
      valueFor(source, "storeName", "store_name"),
      "Warung Sedap Sayur",
    ),
    storeTagline: text(
      valueFor(source, "storeTagline", "store_tagline"),
      "Segar untuk keluarga, mudah dari cabang terdekat.",
    ),
    liveStream:
      (liveStreamPlatform === "YOUTUBE" ||
        liveStreamPlatform === "TIKTOK") &&
      liveStreamEmbedUrl.startsWith("https://") &&
      liveStreamOriginalUrl.startsWith("https://")
        ? {
            enabled: true,
            platform: liveStreamPlatform,
            originalUrl: liveStreamOriginalUrl,
            embedUrl: liveStreamEmbedUrl,
            title: text(
              valueFor(liveStreamSource, "title"),
              "Sedap Sayur Live",
            ),
          }
        : undefined,
    branches,
    activeBranch,
    categories,
    navigationCategories,
    allCategories,
    sections: list(
      valueFor(source, "sections", "homepageSections", "homepage_sections"),
    ).map((item, index) => {
      const section = record(item);
      return {
        id: text(valueFor(section, "id"), String(index)),
        title: text(valueFor(section, "title"), "Pilihan Warung Sedap Sayur"),
        subtitle: text(valueFor(section, "subtitle")) || undefined,
        type: text(
          valueFor(section, "type", "sectionType", "section_type"),
          "FEATURED_PRODUCTS",
        ),
        categoryId:
          (valueFor(section, "categoryId", "category_id") as
            Identifier | undefined) ?? undefined,
        mediaId:
          (valueFor(section, "mediaId", "media_id") as
            Identifier | undefined) ?? undefined,
        ctaText: text(valueFor(section, "ctaText", "cta_text")) || undefined,
        ctaUrl: text(valueFor(section, "ctaUrl", "cta_url")) || undefined,
        products: list(valueFor(section, "products")).map(normalizeProduct),
      };
    }),
    products: list(valueFor(source, "products")).map(normalizeProduct),
    paymentMethods: list(
      valueFor(source, "paymentMethods", "payment_methods"),
    ).map((item) => {
      const method = record(item);
      return {
        id: text(valueFor(method, "id"), ""),
        name: text(valueFor(method, "name"), "Pembayaran manual"),
        type: text(valueFor(method, "type"), "MANUAL"),
        instructions: text(valueFor(method, "instructions")) || undefined,
        accountName:
          text(valueFor(method, "accountName", "account_name")) || undefined,
        accountNumber:
          text(valueFor(method, "accountNumber", "account_number")) ||
          undefined,
        mediaId:
          (valueFor(method, "mediaId", "media_id") as Identifier | undefined) ??
          undefined,
      };
    }),
    deliverySettings: normalizeDelivery(delivery),
    orderLookupMode: ["ORDER_ONLY", "PHONE_ONLY", "ORDER_AND_PHONE"].includes(
      text(valueFor(source, "orderLookupMode", "order_lookup_mode")),
    )
      ? (text(
          valueFor(source, "orderLookupMode", "order_lookup_mode"),
        ) as StoreBootstrap["orderLookupMode"])
      : "ORDER_AND_PHONE",
    degraded: boolean(valueFor(source, "degraded")),
    degradedReason:
      text(valueFor(source, "degradedReason", "degraded_reason")) ||
      undefined,
  };
}

export function normalizeSession(value: unknown): SessionUser {
  const source = record(value);
  const branch = record(valueFor(source, "branch"));
  return {
    id: text(valueFor(source, "id"), ""),
    name: text(valueFor(source, "name"), "Pengguna Warung Sedap Sayur"),
    username: text(valueFor(source, "username")),
    role:
      text(valueFor(source, "role"), "ADMIN").toUpperCase() === "OWNER"
        ? "OWNER"
        : "ADMIN",
    branchId:
      (valueFor(source, "branchId", "branch_id") as Identifier | undefined) ??
      (valueFor(branch, "id") as Identifier | undefined),
    branchName:
      text(valueFor(source, "branchName", "branch_name")) ||
      text(valueFor(branch, "name")) ||
      undefined,
  };
}

export function normalizeOrder(value: unknown): Order {
  const source = record(value);
  return {
    id: (valueFor(source, "id") as Identifier | undefined) ?? undefined,
    orderNumber: text(valueFor(source, "orderNumber", "order_number"), "-"),
    branchId:
      (valueFor(source, "branchId", "branch_id") as Identifier | undefined) ??
      undefined,
    branchName: text(
      valueFor(source, "branchName", "branch_name"),
      "Cabang Warung Sedap Sayur",
    ),
    branchWhatsapp:
      text(valueFor(source, "branchWhatsapp", "branch_whatsapp")) || undefined,
    customerName: text(
      valueFor(
        source,
        "customerName",
        "customer_name",
        "customer_name_snapshot",
      ),
      "Pelanggan",
    ),
    customerWhatsapp:
      text(
        valueFor(
          source,
          "customerWhatsapp",
          "customer_whatsapp",
          "customer_whatsapp_snapshot",
        ),
      ) || undefined,
    customerWhatsappMasked:
      text(
        valueFor(source, "customerWhatsappMasked", "customer_whatsapp_masked"),
      ) || undefined,
    deliveryType: text(
      valueFor(source, "deliveryType", "delivery_type"),
      "PICKUP",
    ),
    deliveryAddress:
      text(valueFor(source, "deliveryAddress", "delivery_address")) ||
      undefined,
    subtotal: numeric(valueFor(source, "subtotal")),
    deliveryFee: numeric(valueFor(source, "deliveryFee", "delivery_fee")),
    discount: numeric(valueFor(source, "discount")),
    pointsRedeemed: numeric(
      valueFor(source, "pointsRedeemed", "points_redeemed"),
    ),
    pointsEarned: numeric(valueFor(source, "pointsEarned", "points_earned")),
    total: numeric(valueFor(source, "total")),
    orderStatus: text(valueFor(source, "orderStatus", "order_status"), "NEW"),
    paymentStatus: text(
      valueFor(source, "paymentStatus", "payment_status"),
      "UNPAID",
    ),
    paymentMethodName: text(
      valueFor(source, "paymentMethodName", "payment_method_name"),
      "Pembayaran manual",
    ),
    createdAt: text(valueFor(source, "createdAt", "created_at")) || undefined,
    updatedAt: text(valueFor(source, "updatedAt", "updated_at")) || undefined,
    confirmedAt:
      text(valueFor(source, "confirmedAt", "confirmed_at")) || undefined,
    serviceTimeZone:
      text(
        valueFor(
          source,
          "serviceTimeZone",
          "service_timezone",
          "resolvedTimeZone",
          "resolved_time_zone",
        ),
      ) || undefined,
    statusEvents: list(valueFor(source, "statusEvents", "status_events"))
      .map((item) => {
        const event = record(item);
        return {
          status: text(valueFor(event, "status", "toStatus", "to_status")),
          createdAt: text(valueFor(event, "createdAt", "created_at")),
        };
      })
      .filter((event) => event.status && event.createdAt),
    liveToken: text(valueFor(source, "liveToken", "live_token")) || undefined,
    liveExpiresAt:
      text(valueFor(source, "liveExpiresAt", "live_expires_at")) || undefined,
    whatsappUrl:
      text(valueFor(source, "whatsappUrl", "whatsapp_url")) || undefined,
    whatsappNotifications: list(
      valueFor(source, "whatsappNotifications", "whatsapp_notifications"),
    ).map((item) => {
      const notification = record(item);
      return {
        recipientType: text(
          valueFor(notification, "recipientType", "recipient_type"),
          "BRANCH",
        ) as "BRANCH" | "OWNER" | "CUSTOMER",
        status: text(valueFor(notification, "status"), "PENDING") as
          "PENDING" | "SENT" | "FAILED" | "MANUAL",
      };
    }),
    items: list(valueFor(source, "items", "orderItems", "order_items")).map(
      (item) => {
        const line = record(item);
        const quantity = numeric(valueFor(line, "quantity"), 1);
        const unitPrice = numeric(valueFor(line, "unitPrice", "unit_price"));
        return {
          id: (valueFor(line, "id") as Identifier | undefined) ?? undefined,
          productId:
            (valueFor(line, "productId", "product_id") as
              Identifier | undefined) ?? undefined,
          productName: text(
            valueFor(
              line,
              "productName",
              "product_name",
              "product_name_snapshot",
              "name",
            ),
            "Produk",
          ),
          quantity,
          unitPrice,
          subtotal: numeric(valueFor(line, "subtotal"), quantity * unitPrice),
        };
      },
    ),
  };
}

export function normalizeCollection(
  value: unknown,
): CollectionResult<ApiRecord> {
  const source = record(value);
  const candidate = valueFor(source, "items", "results", "rows", "data");
  const firstNestedList = Object.values(source).find(Array.isArray);
  const items = Array.isArray(value)
    ? value
    : Array.isArray(candidate)
      ? candidate
      : Array.isArray(firstNestedList)
        ? firstNestedList
        : Object.keys(source).length
          ? [source]
          : [];
  return {
    items: items.map(record),
    total: numeric(valueFor(source, "total", "count"), items.length),
  };
}
